import { defineStore } from 'pinia'
import { checkDocument } from '@js/helpers.ts'

export const useJivoStore = defineStore('jivoStore', {
    state: () => ({
        hasUnreadMessages: false,
        unreadCount: 0,
        chatMode: null,        // 'online' | 'offline' | null
        isLoaded: false,

        // внутренние флаги
        _callbacksInstalled: false,
        _listenersBound: false,

        // ссылки на обработчики для корректного removeEventListener
        __handleLoaded: null,
        __handleIncoming: null,
        __handleOpen: null,
    }),
    actions: {
        init() {
            if (!checkDocument()) return
            this.installCallbacks()
            this.bindWindowEvents()
            this.syncFromApi() // если API уже готов — сразу подтянем статус/непрочитанные
        },

        teardown() {
            if (!checkDocument()) return
            this.unbindWindowEvents()
        },

        // --- Публичные методы управления чатом ---
        openJivoChat() {
            this.hasUnreadMessages = false
            this.unreadCount = 0
            if (!checkDocument()) return

            if (window.jivo_api && typeof window.jivo_api.open === 'function') {
                window.jivo_api.open({ start: 'chat' })
            } else {
                console.warn('Jivo API не инициализирован')
            }
        },

        markAllRead() {
            this.unreadCount = 0
            this.hasUnreadMessages = false
        },

        // --- Внутренняя установка глобальных колбэков Jivo ---
        installCallbacks() {
            if (!checkDocument() || this._callbacksInstalled || window.__jivoCallbacksInstalled) return

            window.jivo_onLoadCallback = () => {
                try {
                    const mode = window.jivo_api?.chatMode?.()
                    const unread = window.jivo_api?.getUnreadMessagesCount?.() ?? 0
                    window.dispatchEvent(new CustomEvent('jivo:loaded', { detail: { mode, unread } }))
                } catch {
                    window.dispatchEvent(new CustomEvent('jivo:loaded', { detail: {} }))
                }
            }

            window.jivo_onMessageReceived = () => {
                window.dispatchEvent(new CustomEvent('jivo:incoming'))
            }

            window.jivo_onOpen = () => {
                window.dispatchEvent(new CustomEvent('jivo:open'))
            }

            window.__jivoCallbacksInstalled = true
            this._callbacksInstalled = true
        },

        bindWindowEvents() {
            if (!checkDocument() || this._listenersBound) return

            this.__handleLoaded = (e) => this.handleLoaded(e)
            this.__handleIncoming = () => this.handleIncoming()
            this.__handleOpen = () => this.handleOpen()

            window.addEventListener('jivo:loaded', this.__handleLoaded)
            window.addEventListener('jivo:incoming', this.__handleIncoming)
            window.addEventListener('jivo:open', this.__handleOpen)

            this._listenersBound = true
        },

        unbindWindowEvents() {
            if (!checkDocument() || !this._listenersBound) return

            window.removeEventListener('jivo:loaded', this.__handleLoaded)
            window.removeEventListener('jivo:incoming', this.__handleIncoming)
            window.removeEventListener('jivo:open', this.__handleOpen)

            this.__handleLoaded = null
            this.__handleIncoming = null
            this.__handleOpen = null
            this._listenersBound = false
        },

        // --- Хэндлеры событий ---
        handleLoaded(e) {
            const mode = e?.detail?.mode ?? null
            const unread = Number(e?.detail?.unread ?? 0)
            if (mode) this.chatMode = mode
            this.unreadCount = unread
            this.hasUnreadMessages = unread > 0
            this.isLoaded = true
        },

        handleIncoming() {
            this.unreadCount += 1
            this.hasUnreadMessages = this.unreadCount > 0
        },

        handleOpen() {
            this.markAllRead()
        },

        // --- Разовая синхронизация при init(), если API уже готов ---
        syncFromApi() {
            try {
                if (!window.jivo_api) return
                const mode = window.jivo_api?.chatMode?.()
                const unread = Number(window.jivo_api?.getUnreadMessagesCount?.() ?? 0)
                if (mode) this.chatMode = mode
                this.unreadCount = unread
                this.hasUnreadMessages = unread > 0
            } catch {}
        },
    },
})
